import { useCallback, useEffect, useState } from 'react'

import { ISegment } from '@/types/highlight.interface'
import { ITranscription } from '@/types/task.interface'

import { getChunkText, getSelectedSegmentFromTextOffsets } from '../helpers'

interface IPendingSegment {
	description: string
	start: number
	end: number
}

type THighlightMenuContainer = 'original' | 'translation'

interface IHighlightContextMenu {
	x: number
	y: number
	strategy: 'absolute' | 'fixed'
	container?: THighlightMenuContainer
}

const getClosestChunkElement = (node: Node | null) => {
	if (!node) return null

	const element =
		node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element)

	return (
		element?.closest<HTMLElement>('[data-transcription-chunk-index]') || null
	)
}

const getOffsetInsideElement = (
	element: HTMLElement,
	node: Node,
	offset: number
) => {
	const range = document.createRange()

	range.selectNodeContents(element)
	range.setEnd(node, offset)

	return range.toString().length
}

const getOffsetInsideChunkText = ({
	chunkElement,
	node,
	offset,
	chunkTextLength,
	boundary
}: {
	chunkElement: HTMLElement
	node: Node
	offset: number
	chunkTextLength: number
	boundary: 'start' | 'end'
}) => {
	const textElement = chunkElement.querySelector<HTMLElement>(
		'[data-transcription-text]'
	)

	if (!textElement) {
		return boundary === 'start' ? 0 : chunkTextLength
	}

	if (textElement.contains(node)) {
		return getOffsetInsideElement(textElement, node, offset)
	}

	const element =
		node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element)

	if (!element) {
		return boundary === 'start' ? 0 : chunkTextLength
	}

	const position = element.compareDocumentPosition(textElement)

	if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
		return 0
	}

	if (position & Node.DOCUMENT_POSITION_PRECEDING) {
		return chunkTextLength
	}

	return boundary === 'start' ? 0 : chunkTextLength
}

export const useHighlightSegmentSelection = () => {
	const [pendingSegment, setPendingSegment] = useState<IPendingSegment | null>(
		null
	)
	const [highlightMenu, setHighlightMenu] =
		useState<IHighlightContextMenu | null>(null)

	const closeHighlightMenu = useCallback(() => {
		setPendingSegment(null)
		setHighlightMenu(null)
		window.getSelection()?.removeAllRanges()
	}, [])

	useEffect(() => {
		if (!highlightMenu || !pendingSegment) return

		const handleSelectionChange = () => {
			const selection = window.getSelection()

			if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
				closeHighlightMenu()
			}
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeHighlightMenu()
			}
		}

		document.addEventListener('selectionchange', handleSelectionChange)
		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('selectionchange', handleSelectionChange)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [highlightMenu, pendingSegment, closeHighlightMenu])

	const openFolderMenuForSegment = useCallback(
		(segment: ISegment, menuPosition: IHighlightContextMenu) => {
			setPendingSegment(segment)
			setHighlightMenu(menuPosition)
		},
		[]
	)

	const selectSegmentFromTranscript = useCallback(
		(
			container?: HTMLElement | null,
			menuContainer: THighlightMenuContainer = 'translation',
			items: ITranscription[] = []
		) => {
			if (!container) return

			const selection = window.getSelection()
			if (!selection?.rangeCount) return

			const range = selection.getRangeAt(0)
			if (range.collapsed) return

			const startChunkElement = getClosestChunkElement(range.startContainer)
			const endChunkElement = getClosestChunkElement(range.endContainer)

			if (!startChunkElement || !endChunkElement) return

			if (
				!container.contains(startChunkElement) ||
				!container.contains(endChunkElement)
			) {
				return
			}

			const startIndex = Number(
				startChunkElement.dataset.transcriptionChunkIndex
			)
			const endIndex = Number(endChunkElement.dataset.transcriptionChunkIndex)

			if (Number.isNaN(startIndex) || Number.isNaN(endIndex)) return

			const fromIndex = Math.min(startIndex, endIndex)
			const toIndex = Math.max(startIndex, endIndex)

			let start = Infinity
			let end = 0

			const selectedTextParts: string[] = []

			for (let index = fromIndex; index <= toIndex; index++) {
				const chunk = items[index]
				if (!chunk) continue

				const chunkTextLength = getChunkText(chunk).length

				const from =
					index === startIndex
						? getOffsetInsideChunkText({
								chunkElement: startChunkElement,
								node: range.startContainer,
								offset: range.startOffset,
								chunkTextLength,
								boundary: 'start'
						  })
						: 0

				const to =
					index === endIndex
						? getOffsetInsideChunkText({
								chunkElement: endChunkElement,
								node: range.endContainer,
								offset: range.endOffset,
								chunkTextLength,
								boundary: 'end'
						  })
						: chunkTextLength

				const selectedSegments = getSelectedSegmentFromTextOffsets({
					chunk,
					from,
					to
				})

				for (const segment of selectedSegments) {
					start = Math.min(start, segment.start)
					end = Math.max(end, segment.end)
					selectedTextParts.push(segment.content)
				}
			}

			const text = selectedTextParts.join('').trim()

			if (end < start || !text) return

			const rect = range.getBoundingClientRect()
			const containerRect = container.getBoundingClientRect()

			const menuWidth = 310
			const menuHeight = 320
			const gapBelow = 10
			const gapAbove = 8
			const padding = 8

			const visibleSelectionTop = Math.max(
				rect.top,
				containerRect.top + padding
			)
			const visibleSelectionBottom = Math.min(
				rect.bottom,
				containerRect.bottom - padding
			)

			const visibleSelectionLeft = Math.max(
				rect.left,
				containerRect.left + padding
			)
			const visibleSelectionRight = Math.min(
				rect.right,
				containerRect.right - padding
			)

			const rawX =
				visibleSelectionLeft -
				containerRect.left +
				container.scrollLeft +
				(visibleSelectionRight - visibleSelectionLeft) / 2

			const minX = container.scrollLeft + padding + menuWidth / 2
			const maxX =
				container.scrollLeft + container.clientWidth - padding - menuWidth / 2

			const x = Math.min(Math.max(rawX, minX), maxX)

			const spaceBelow = containerRect.bottom - visibleSelectionBottom
			const spaceAbove = visibleSelectionTop - containerRect.top

			let y: number

			if (spaceBelow >= menuHeight + gapBelow) {
				y =
					visibleSelectionBottom -
					containerRect.top +
					container.scrollTop +
					gapBelow
			} else if (spaceAbove >= menuHeight + gapAbove) {
				y =
					visibleSelectionTop -
					containerRect.top +
					container.scrollTop -
					menuHeight -
					gapAbove
			} else {
				y =
					container.scrollTop +
					Math.max(padding, (container.clientHeight - menuHeight) / 2)
			}

			openFolderMenuForSegment(
				{
					start,
					end,
					description: text
				},
				{
					x,
					y,
					strategy: 'absolute',
					container: menuContainer
				}
			)
		},
		[openFolderMenuForSegment]
	)

	const selectSegmentFromTimeRange = useCallback(
		(
			timeStart: string,
			timeEnd: string,
			translation: ITranscription[],
			menuPosition: { x: number; y: number }
		) => {
			const parseTime = (str: string) => {
				const parts = str.split(':').map(Number)
				return parts[0] * 3600 + parts[1] * 60 + parts[2]
			}

			const start = parseTime(timeStart)
			const end = parseTime(timeEnd)

			if (end <= start) return

			let text = ''

			for (const line of translation) {
				if (end >= line.start && start <= line.end) {
					for (const segment of line.segments) {
						if (end >= segment.start && start <= segment.end) {
							text += segment.content
						}
					}
				}
			}

			openFolderMenuForSegment(
				{
					start,
					end,
					description: text.trim()
				},
				{
					...menuPosition,
					strategy: 'fixed'
				}
			)
		},
		[openFolderMenuForSegment]
	)

	return {
		pendingSegment,
		highlightMenu,
		closeHighlightMenu,
		selectSegmentFromTranscript,
		selectSegmentFromTimeRange
	}
}
